<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { formatDate } from '@lib/utils/format-date'
import capitalize from 'lodash/capitalize'
import formatDollar from '@lib/utils/format-dollar'
import get from 'lodash/get'

export default {
  name: 'ProductDetails',
  props: {
    providerId: {
      type: String,
      default: '',
    },
    providerProductId: {
      type: String,
      default: '',
    },
    provider: {
      type: Object,
      default: () => ({}),
    },
    supplierId: {
      type: String,
      default: '',
    },
    sku: {
      type: String,
      default: '',
    },
    blacklist: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      required: true,
      validator: v => !v || /supplier|provider/.test(v),
    },
    product: {
      type: Object,
      default: () => null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modals: {
        delete: false,
      },
    }
  },
  computed: {
    ...mapState('suppliers', ['suppliers']),
    ...mapGetters('me', ['canReadSupplier']),
    status() {
      const { availability } = this.product

      if (availability && availability.enabled) {
        return 'available'
      }

      return 'unavailable'
    },
    shippingData() {
      if (this.loading) {
        return {}
      }

      const { shipping } = this.product
      const { dimensions, weight } = shipping
      const shippingCostAfterTax = shipping.shipping_after_tax
      const shippingCostBeforeTax = shipping.shipping_before_tax

      return {
        shippingCostAfterTax: shippingCostAfterTax
          ? formatDollar(shippingCostAfterTax)
          : '—',
        shippingCostBeforeTax: shippingCostBeforeTax
          ? formatDollar(shippingCostBeforeTax)
          : '—',
        length: this.productDimensions('product.shipping', 'length'),
        height: this.productDimensions('product.shipping', 'height'),
        depth: this.productDimensions('product.shipping', 'depth'),
        unit: this.formatDimension(dimensions.unit),
        weightUnit: this.formatDimension(weight.unit),
        weight: this.productWeight('product.shipping'),
      }
    },
    pricingData() {
      const { pricing } = this.product
      const keys = Object.keys(pricing)
      const priceAfterTax = pricing.price_after_tax
      const priceBeforeTax = pricing.price_before_tax
      const salesTax = priceAfterTax - priceBeforeTax
      const formattedPricing = {}

      for (const key in keys) {
        const val = keys[key]
        formattedPricing[val] = pricing[val] ? formatDollar(pricing[val]) : '—'
      }

      if (salesTax > 0) {
        formattedPricing.salesTax = formatDollar(salesTax)
      } else {
        formattedPricing.salesTax = '—'
      }

      return formattedPricing
    },
    supplierName() {
      return this.product.supplier_name
    },
    computedSupplierId() {
      return this.supplierId || get(this.product, 'supplier_id')
    },
    contract() {
      if (
        !get(this.provider, 'name') ||
        !this.supplierName
      ) {
        return
      }

      return `${this.provider.name} / ${this.supplierName}`
    },
    categories() {
      if (this.providerProductId) {
        return [...new Set(get(this.product, 'categories', []).map(prod => prod.name))]
      }
      return this.product && this.product.categories
    },
  },
  methods: {
    ...mapActions('suppliers', ['getSupplier']),
    capitalize,
    formatDollar,
    formatDate(value, format) {
      return formatDate(value, format || 'DD/MM/YYYY')
    },
    formatDimension(value) {
      switch (value) {
        case 'cm':
          return 'cm - Centimeters'
        case 'in':
          return 'in - Inches'
        case 'lb':
          return 'lb - Pounds'
        case 'kg':
          return 'kg - Kilograms'
        default:
          return '—'
      }
    },
    onMenuSelect(name) {
      const { type, providerId, providerProductId, computedSupplierId, sku } = this

      switch (name) {
        case 'edit':
          this.$router.push({
            name: `${type}-product-edit`,
            params: { providerId, providerProductId, supplierId: computedSupplierId, sku },
          })
          break
        case 'delete':
          this.$modal.show('delete-product', {
            props: {
              providerId: this.providerId,
              provideProductId: this.provideProductId,
              supplierId: this.computedSupplierId,
              sku: this.sku,
              type: this.type,
            },
          })
          break
      }
    },
    productWeight(target) {
      const { weight } = get(this, target)

      if (!weight) {
        return '—'
      }

      const { value, unit } = weight

      if (value) {
        return `${value} ${unit}`
      }

      return '—'
    },
    productDimensions(target, value) {
      const { dimensions } = get(this, target)

      if (!dimensions) return '—'

      const { unit } = dimensions

      return dimensions[value] && unit ? `${dimensions[value]} ${unit}` : '—'
    },
    onSupplierClick() {
      if (this.canReadSupplier) {
        this.$router.push({ name: 'supplier-details', params: { id: this.product.supplier_id } })
      } else {
        this.$notify({
          type: 'error',
          title: 'Forbidden',
          message: 'You don\'t have permission',
        })
      }
    },
  },
}
</script>

<template>
  <data-box
    v-if="!loading"
    :header="`${capitalize(type)} Product Details`"
    :status="status"
  >
    <data-list>
      <dt>Date Created</dt>
      <dd>{{ formatDate(product.created_at, 'DD/MM/YYYY HH:mm') || '—' }}</dd>

      <dt>{{ capitalize(type) }} Product ID</dt>
      <dd>{{ product.id || '—' }}</dd>

      <template v-if="blacklist.indexOf('contract_id') === -1">
        <dt>Contract</dt>
        <dd>
          <component
            :is="contract ? 'router-link' : 'span'"
            :to="{ path: `/contracts/${product.contract_id}` }"
          >
            {{ contract || '—' }}
          </component>
        </dd>
      </template>

      <template v-if="type === 'provider'">
        <dt>Provider Name</dt>
        <dd>
          <component
            :is="provider && providerId ? 'router-link' : 'span'"
            :to="{ path: `/providers/${providerId}` }"
          >
            {{ (provider && provider.name) || '—' }}
          </component>
        </dd>
      </template>

      <dt>Supplier Name</dt>
      <dd>
        <component
          :is="product.supplier_id ? 'router-link' : 'span'"
          :to="''"
          @click.native="onSupplierClick"
        >
          {{ supplierName }}
        </component>
      </dd>

      <template v-if="blacklist.indexOf('supplier_product_id') === -1">
        <dt>Original Supplier Product Name</dt>
        <dd>
          <component
            :is="product.supplier_product_name && product.supplier_id ? 'router-link' : 'span'"
            :to="{ path: `/supplier-product/${product.supplier_id}/${product.supplier_product_sku}` }"
          >
            {{ product.supplier_product_name || '—' }}
          </component>
        </dd>
      </template>

      <dt>{{ capitalize(type) }} Product Name</dt>
      <dd>{{ product.name || '—' }}</dd>

      <dt>Description</dt>
      <dd>{{ product.description || '—' }}</dd>

      <dt>Product Available</dt>
      <dd>
        {{
          product.availability && product.availability.enabled
            ? 'Yes'
            : 'No' || '—'
        }}
      </dd>

      <dt>Availability Status</dt>
      <dd>
        {{ (product.availability && product.availability.status) || '—' }}
      </dd>

      <dt>Availability Start Date</dt>
      <dd>
        {{
          formatDate(
            product.availability && product.availability.start_date
          ) || '—'
        }}
      </dd>

      <dt>Availability End Date</dt>
      <dd>
        {{
          formatDate(product.availability && product.availability.end_date) ||
            '—'
        }}
      </dd>

      <dt>Allocated Stock Quantity</dt>
      <dd>
        {{ product.allocated_stock_qty || '—' }}
      </dd>

      <template v-if="type === 'supplier'">
        <dt>Lifetime Sales</dt>
        <dd>
          {{ product.lifetime_sales || '—' }}
        </dd>
      </template>

      <dt>Image URL/s</dt>
      <dd>
        <template v-if="product.images && product.images.length">
          <a
            v-for="(image, index) in product.images"
            :key="index"
            :href="image"
            :class="$style.link"
            target="_blank"
          >{{ image }}</a>
        </template>
        <span v-else>-</span>
      </dd>

      <dt>Brand</dt>
      <dd>{{ product.brand || '—' }}</dd>

      <dt>SKU</dt>
      <dd>{{ product.sku || '—' }}</dd>

      <dt>GTIN</dt>
      <dd>{{ product.gtin || '—' }}</dd>

      <dt>Model</dt>
      <dd>{{ product.model || '—' }}</dd>

      <dt>MPN</dt>
      <dd>{{ product.mpn || '—' }}</dd>

      <dt>Categories</dt>
      <dd v-if="categories && categories.length">
        {{ categories.join(', ') }}
      </dd>
      <dd v-else>
        —
      </dd>

      <dt>Last Updated</dt>
      <dd>{{ formatDate(product.updated_at, 'DD/MM/YYYY HH:mm') || '—' }}</dd>
    </data-list>

    <hr :class="['divider-primary', 'info-block__divider']">

    <h3 :class="$style.header">
      Price
    </h3>
    <data-list>
      <dt>Currency</dt>
      <dd>{{ product.currency || '—' }}</dd>

      <dt>Sales Tax</dt>
      <dd>{{ product.sales_tax_applicable || '—' }}</dd>

      <dt>Before Tax Cost Price</dt>
      <dd>{{ pricingData.cost }}</dd>

      <dt>Price Before Tax</dt>
      <dd>{{ pricingData.price_before_tax }}</dd>

      <dt>Price After Tax</dt>
      <dd>{{ pricingData.price_after_tax }}</dd>

      <dt>RRP Before Tax</dt>
      <dd>{{ pricingData.rrp_before_tax }}</dd>

      <dt>RRP After Tax</dt>
      <dd>{{ pricingData.rrp_after_tax }}</dd>
    </data-list>

    <hr :class="['divider-primary', 'info-block__divider']">

    <h3 :class="$style.header">
      Dimensions
    </h3>
    <data-list>
      <dt>Length</dt>
      <dd>{{ productDimensions('product', 'length') }}</dd>

      <dt>Height</dt>
      <dd>{{ productDimensions('product', 'height') }}</dd>

      <dt>Depth</dt>
      <dd>{{ productDimensions('product', 'depth') }}</dd>

      <dt>Weight</dt>
      <dd>{{ productWeight('product') }}</dd>
    </data-list>

    <hr :class="['divider-primary', 'info-block__divider']">

    <h3 :class="$style.header">
      Shipping
    </h3>
    <data-list>
      <dt>Shipping Cost Before Tax</dt>
      <dd>{{ shippingData.shippingCostBeforeTax }}</dd>

      <dt>Shipping Cost After Tax</dt>
      <dd>{{ shippingData.shippingCostAfterTax }}</dd>

      <dt>Length</dt>
      <dd>{{ shippingData.length }}</dd>

      <dt>Height</dt>
      <dd>{{ shippingData.height }}</dd>

      <dt>Depth</dt>
      <dd>{{ shippingData.depth }}</dd>

      <dt>Weight</dt>
      <dd>{{ shippingData.weight }}</dd>
    </data-list>
  </data-box>
</template>

<style lang="scss" module>
.header {
  margin-bottom: rem(44px);
}
.link {
  display: block;
}
</style>
